/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */

// import '@babel/polyfill'
import React from 'react'
import { navigate } from 'gatsby'
import { ReactNotifications } from 'react-notifications-component'
import 'react-notifications-component/dist/scss/notification.scss'
import 'animate.css'
import AvailabilityProvider from './src/hooks/availabilityContext'
import WorkerAffordabilityProvider from './src/hooks/workerAffordabilityContext'
import { AuthProvider } from './src/hooks/authContext'
import { passwordProtected } from './src/config/password-proctected'
import { getCookie, getCookieNames } from './src/utils/cookieHelper'
import { head, filter, isEmpty } from 'lodash'
// Ensure feature flags module is loaded in the browser
import { initializeFlagOverridesFromURL, setFlagRouteContext } from './src/config/featureFlags'
import { trackHubSpotPageView } from './src/utils/hubspot'
import HubSpotTracker from './src/components/hubspotTracker'

export const wrapRootElement = ({ element }) => (
  <AuthProvider>
    <HubSpotTracker />
    <AvailabilityProvider>
      <WorkerAffordabilityProvider>
        <ReactNotifications />
        {element}
      </WorkerAffordabilityProvider>
    </AvailabilityProvider>
  </AuthProvider>
)

// Helper function to check and redirect if unauthorized
// Used by both onInitialClientRender and onPreRouteUpdate
const checkRouteAccess = (pathname, isInitialMount = false) => {
  if (typeof window === 'undefined') return;
  
  // Extract alias from pathname (format: /{alias}/...)
  const pathSegments = pathname.split('/').filter(Boolean);
  if (pathSegments.length === 0) return; // Root path
  
  const alias = pathSegments[0];
  
  // CRITICAL: Exclude auth pages (signin, signout, unauthorized) from route guard
  // These pages should always be accessible to prevent redirect loops
  const isAuthPage = pathSegments.length > 1 && 
    ['signin', 'signout', 'unauthorized'].includes(pathSegments[1]);
  if (isAuthPage) return; // Allow access to auth pages
  
  // Check if this alias is password-protected
  const isProtected = !isEmpty(head(filter(passwordProtected, ['lga', alias])));
  
  if (!isProtected) return; // Not a protected page, allow navigation
  
  // Check if user has auth cookie with valid value (not "false")
  // Use synchronous helper to check feature flag in non-React context
  const cookieNames = getCookieNames();
  const fahousingCookie = getCookie(cookieNames.fahousing);
  const hasCookie = fahousingCookie && fahousingCookie !== 'false';
  
  if (!hasCookie) {
    navigate(`/${alias}/signin`, { replace: true });
    return;
  }
  
  // CRITICAL: Always skip unauthorized check when we have a valid cookie
  // This prevents flash of unauthorized page when:
  // - User logs in as admin in another app via SSO
  // - Opens new tab to our app (sessionStorage may have stale normal user data or be empty)
  // - Navigates to protected route (sessionStorage may have stale normal user data)
  // - Refreshes page (sessionStorage may have stale normal user data)
  // 
  // The problem: Cookies are shared across tabs/apps, but sessionStorage is per-tab.
  // When admin signs in via SSO in another app, the cookie is set (shared), but
  // sessionStorage in this tab might have stale data (from before admin login) or be empty.
  // 
  // Solution: Always let AuthContext and useAuthRedirect handle the access check after
  // fresh user data is fetched. This ensures we never redirect based on stale cached data.
  // 
  // AuthContext will:
  // - On mount: Clear stale cached user if cookie is valid (forces fresh fetch)
  // - Fetch fresh user data from backend
  // 
  // useAuthRedirect will:
  // - Wait for fresh user data to load
  // - Only redirect to /unauthorized after confirming user doesn't have access with fresh data
  // 
  // This eliminates the flash of /unauthorized → /signin → /client-home
  // Instead: direct navigation → /client-home (or /unauthorized if truly no access)
  return; // Always let components handle access check with fresh data
}

// Handle initial page load (first visit, direct URL access, page refresh)
// This fires once when the app first loads on the client
export const onInitialClientRender = () => {
  if (typeof window !== 'undefined') {
    // Pass isInitialMount=true to skip unauthorized check on initial mount
    // This prevents flash of unauthorized page when cached user data might be stale
    checkRouteAccess(window.location.pathname, true);
  }
}

// BEST PRACTICE: Route guard using onPreRouteUpdate
// This intercepts client-side navigation BEFORE route components load, preventing chunk loading issues
// This handles Link clicks, navigate() calls, and programmatic navigation
export const onPreRouteUpdate = ({ location, prevLocation }) => {
  // If prevLocation is null, this is an initial load (not a navigation)
  // Treat it as initial mount to skip unauthorized check and prevent flash
  const isInitialMount = prevLocation === null;
  checkRouteAccess(location.pathname, isInitialMount);
}

// Handle chunk loading errors gracefully
// This can happen when navigating between pages and chunks fail to load
// (e.g., when redirecting interrupts chunk loading, or service worker cache issues)
// Set clientAlias / topicAlias in the OpenFeature global context on every Gatsby navigation.
// The web SDK is context-driven — per-call context is ignored, so this is the only way
// to make ConfigCat targeting rules that reference clientAlias/topicAlias work correctly.
export const onRouteUpdate = ({ location, prevLocation }) => {
  setFlagRouteContext(location.pathname);

  // Track a HubSpot page view on client-side navigation only. The initial page
  // load is auto-tracked by the HubSpot loader script (prevLocation === null),
  // so counting it here too would double-count the landing page.
  if (prevLocation) {
    trackHubSpotPageView(`${location.pathname}${location.search || ''}`);
  }
};

export const onClientEntry = () => {
  // Initialize flag overrides from URL on every page load (irrespective of whether that page has a flag)
  initializeFlagOverridesFromURL();
  // Set route context for the initial page load (onRouteUpdate doesn't fire on first render)
  setFlagRouteContext(window.location.pathname);
  
  if (typeof window !== 'undefined') {
    let reloadAttempted = false;
    const MAX_RELOAD_ATTEMPTS = 1;
    let reloadCount = 0;

    const handleChunkError = async () => {
      // Prevent infinite reload loops
      if (reloadAttempted || reloadCount >= MAX_RELOAD_ATTEMPTS) {
        console.warn('Chunk load error: Max reload attempts reached. Please refresh manually.');
        return;
      }

      reloadAttempted = true;
      reloadCount++;
      console.warn('Chunk load error detected, clearing service worker cache and reloading...');
      
      // Try to unregister service worker if it's causing issues
      if ('serviceWorker' in navigator) {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (let registration of registrations) {
            // Unregister the service worker to clear stale cache
            await registration.unregister();
            console.log('Service worker unregistered');
          }
        } catch (error) {
          console.warn('Error unregistering service worker:', error);
        }
      }
      
      // Clear caches if available
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(
            cacheNames.map(cacheName => caches.delete(cacheName))
          );
          console.log('Caches cleared');
        } catch (error) {
          console.warn('Error clearing caches:', error);
        }
      }
      
      // Use a small delay to avoid immediate reload loops
      setTimeout(() => {
        window.location.reload();
      }, 100);
    };

    window.addEventListener('error', (event) => {
      // Check if this is a ChunkLoadError or ServiceWorker error
      const isChunkError = event.message &&
        (event.message.includes('Loading chunk') ||
          event.message.includes('ChunkLoadError') ||
          event.message.includes('Failed to fetch dynamically imported module') ||
          event.message.includes('ServiceWorker intercepted') ||
          event.message.includes('ServiceWorker'));
      
      if (isChunkError) {
        // Handle JS file chunk errors or service worker errors
        if (event.filename && (event.filename.includes('.js') || event.filename.includes('framework-'))) {
          event.preventDefault();
          handleChunkError();
        }
      }
    });

    // Also handle unhandled promise rejections (ChunkLoadError is often a promise rejection)
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason;
      const errorMessage = error?.message || error?.toString() || '';
      const isChunkError = error &&
        (errorMessage.includes('Loading chunk') ||
          errorMessage.includes('ChunkLoadError') ||
          errorMessage.includes('Failed to fetch dynamically imported module') ||
          errorMessage.includes('ServiceWorker intercepted') ||
          errorMessage.includes('ServiceWorker'));      
      if (isChunkError) {
        event.preventDefault();
        handleChunkError();
      }
    });

    // Reset reload flag on successful navigation
    // This allows one reload attempt per navigation
    if (window.history && window.history.pushState) {
      const originalPushState = window.history.pushState;
      window.history.pushState = function(...args) {
        reloadAttempted = false;
        return originalPushState.apply(window.history, args);
      };
    }
  }
};